import { LoadingTracker, LoadStatus } from './LoadingTracker';
import { observable, action, computed, makeObservable } from 'mobx';
import { IdResource, IResourceSession, PrivateResourceSession } from './ResourceSession';

type ItemsLoaderFunction<T> = () => Promise<T[]>;

export type ResourceCollectionStoreConfig<T> = {
    getItems: ItemsLoaderFunction<T>;
    getKey: (item: T) => unknown;
    session?: IResourceSession<T>;
};

export class ResourceCollectionStore<T> {
    @observable
    items: T[] = [];

    @observable
    loadingTracker: LoadingTracker = new LoadingTracker();

    private fetchItems: ItemsLoaderFunction<T>;
    private session: IResourceSession<T>;
    private keyOf: (item: T) => unknown;

    constructor(config: ResourceCollectionStoreConfig<T>) {
        makeObservable(this);
        this.fetchItems = config.getItems;
        this.keyOf = config.getKey;
        this.session = config.session || new PrivateResourceSession(config.getKey);
    }

    /**
     * Loads and sets the items. If a newer load supersedes this one, resolves with the raw
     * response and leaves the store unchanged; that response is not the session instances.
     */
    async load(): Promise<T[]> {
        return this.loadingTracker.track(async (isCurrent) => {
            const res = await this.fetchItems();
            return isCurrent() ? this.setItems(res) : res;
        });
    }

    @action
    setItems(items: T[]): T[] {
        this.items = this.session.updateItems(items);
        return this.items;
    }

    @action
    updateItem(item: T): T {
        if (!item) return item;
        const sessionItem = this.session.updateItem(item);
        return sessionItem;
    }

    @action
    deleteItem(itemId: unknown): void {
        if (!itemId) return;
        this.items = this.items.filter((i) => this.keyOf(i) !== itemId);
    }

    @action
    addItem(item: T): T {
        const sessionItem = this.session.updateItem(item);
        this.items.push(sessionItem);
        return sessionItem;
    }

    addItemIfExists(item: T | undefined): T | undefined {
        if (item === undefined) return item;
        return this.addItem(item);
    }

    @computed
    get status() {
        return this.loadingTracker.status;
    }

    get lastError() {
        return this.loadingTracker.lastError;
    }

    async ensureLoaded(): Promise<T[]> {
        if (this.status === LoadStatus.Complete) return this.items;

        if (this.status === LoadStatus.Loading) {
            try {
                await this.loadingTracker.asPromise();
            } catch (e) {
                console.log(e);
                return this.items;
            }
        }

        //pending or errored, we just reload
        return this.load();
    }
}
export type IdResourceCollectionStoreConfig<T> = Omit<ResourceCollectionStoreConfig<T>, 'getKey'>;

export class IdResourceCollectionStore<T extends IdResource> extends ResourceCollectionStore<T> {
    constructor(config: IdResourceCollectionStoreConfig<T>) {
        super({
            ...config,
            getKey: (x) => x.id,
        });
    }
}
